import type { AuthConfig } from './auth/pkce.js'

export interface AppConfig {
  readonly auth: AuthConfig
  readonly apiBaseUrl: string
  readonly environment: string
}

/**
 * Whether this console deploys only Qrvey's own test instances.
 *
 * `dev` and `staging` do, which is what lets two defaults lean Qrvey's way
 * there: the run form's Qrvey-internal box (IM-175) and the New customer
 * form's kind (IM-180). Exact names only — anything else, `prod` and an
 * unrecognised name included, answers false, because the customer's answer
 * is the one whose mistake is the loud one.
 */
export const isQrveyTestEnvironment = (environment: string): boolean =>
  environment === 'dev' || environment === 'staging'

/**
 * Configuration is fetched at runtime, not baked in at build time.
 *
 * The API URL and Cognito client id only exist once the stack is deployed, so
 * build-time injection would need deploy, read outputs, rebuild, deploy again.
 * Serving a small JSON file the stack writes alongside the assets means one
 * deploy, and the same bundle is valid in every environment.
 */
const CONFIG_PATH = '/config.json'

const str = (source: Record<string, unknown>, key: string): string => {
  const value = source[key]
  if (typeof value !== 'string' || value === '') {
    throw new Error(`Console configuration is missing ${key}`)
  }
  return value
}

export const fetchConfig = async (fetchImpl: typeof fetch = fetch): Promise<AppConfig> => {
  const response = await fetchImpl(CONFIG_PATH, { cache: 'no-store' })
  if (!response.ok) {
    throw new Error(`Could not load console configuration (${String(response.status)})`)
  }

  const body: unknown = await response.json()
  if (typeof body !== 'object' || body === null) {
    throw new Error('Console configuration was not an object')
  }
  const raw = body as Record<string, unknown>

  return {
    auth: {
      domain: str(raw, 'authDomain'),
      clientId: str(raw, 'clientId'),
      // Derived from where the app is actually served, so a config file copied
      // between environments cannot send the browser to the wrong origin.
      redirectUri: `${window.location.origin}/auth/callback`,
      providerName: typeof raw['idpName'] === 'string' ? raw['idpName'] : 'IdentityCenter',
    },
    apiBaseUrl: str(raw, 'apiBaseUrl'),
    environment: str(raw, 'environment'),
  }
}
